import React, { Suspense, useEffect, useLayoutEffect, useState } from "react";
import { useCartSync } from "@/hooks/useCartSync";
import { useInventoryCacheBuster } from "@/hooks/useInventoryCacheBuster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthProvider";
import Header from "./components/Header";
import ScrollToTop from "./components/ScrollToTop";
import { PendingPurchaseChecker } from "./components/PendingPurchaseChecker";
import ErrorBoundary from "./components/ErrorBoundary";
import NonProductionNoIndex from "./components/NonProductionNoIndex";
import { GeoBlockCheck } from "./components/GeoBlockCheck";
import CanonicalEnforcer from "./components/seo/CanonicalEnforcer";
import FilteredViewNoindex from "./components/seo/FilteredViewNoindex";
import StorefrontRedirector from "./components/StorefrontRedirector";
import LegacyShopifyRedirector from "./components/LegacyShopifyRedirector";

import AnnouncementBanner from "./components/AnnouncementBanner";
import LazyChunkBanner from "./components/LazyChunkBanner";

import { lazyWithRetry } from "./lib/lazyWithRetry";

const ImprovedNav = lazyWithRetry(() => import("./components/ImprovedNav"));

const Footer = lazyWithRetry(() => import("./components/Footer"));
const CookieBanner = lazyWithRetry(() => import("./components/CookieBanner"));
const NotificationPrompt = lazyWithRetry(() => import("./components/NotificationPrompt"));
const ExitIntentEmailCapture = lazyWithRetry(() => import("./components/cart/ExitIntentEmailCapture"));
const Toaster = lazyWithRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazyWithRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

import { staticRoutes } from "./routes/staticRoutes";
import { brandRoutes } from "./routes/brandRoutes";
import { productRoutes } from "./routes/productRoutes";
import { categoryRoutes } from "./routes/categoryRoutes";
import { adminRoutes } from "./routes/adminRoutes";

// Dev-only floating panel som visar vilka analytics-script som blockerats.
const AnalyticsPolicyDebugPanel = import.meta.env.DEV
  ? lazyWithRetry(() => import("./components/dev/AnalyticsPolicyDebugPanel"))
  : null;

const PageLoader = () => (
  <div className="min-h-[calc(100vh-160px)] flex items-center justify-center">
    <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const SmoothRoutes = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {children}
    </Routes>
  </Suspense>
);

const DeferredMount = ({ children, timeout = 3500 }: { children: React.ReactNode; timeout?: number }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    let timerId: ReturnType<typeof globalThis.setTimeout> | undefined;
    let idleId: number | undefined;
    const activate = () => setReady(true);
    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    events.forEach((eventName) => {
      window.addEventListener(eventName, activate, { passive: true, once: true, capture: true });
    });
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(activate, { timeout });
    } else {
      timerId = globalThis.setTimeout(activate, Math.min(timeout, 1800));
    }
    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, activate, { capture: true });
      });
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timerId !== undefined) globalThis.clearTimeout(timerId);
    };
  }, [ready, timeout]);

  return ready ? <>{children}</> : null;
};

const AppShell = ({ children }: { children: (ctx: { isAdmin: boolean; isCheckout: boolean }) => React.ReactNode }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isCheckout = location.pathname.startsWith("/kassa");
  const isCategory = /^\/(hund|katt|smadjur|fagel|akvarium|reptil|hast)(\/|$)/.test(location.pathname);

  useLayoutEffect(() => {
    if (location.pathname === "/") return;
    const s = document.getElementById("shell");
    if (s && !s.classList.contains("shell-hidden")) {
      s.classList.add("shell-hidden");
    }
  }, [location.pathname]);

  return <div className={isCategory ? "category-page-surface" : undefined}>{children({ isAdmin, isCheckout })}</div>;
};


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const CartSyncProvider = ({ children }: { children: React.ReactNode }) => {
  useCartSync();
  useInventoryCacheBuster();
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <DeferredMount timeout={2500}>
          <Suspense fallback={null}><Toaster /></Suspense>
          <Suspense fallback={null}><Sonner /></Suspense>
        </DeferredMount>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <NonProductionNoIndex />
          <StorefrontRedirector />
          <LegacyShopifyRedirector />
          <CanonicalEnforcer />
          <FilteredViewNoindex />
          <GeoBlockCheck />
          <CartSyncProvider>
            <ScrollToTop />
            <PendingPurchaseChecker />
            <LazyChunkBanner />
            <a href="#main-content" className="skip-to-main">
              Hoppa till huvudinnehåll
            </a>
            <AppShell>
              {({ isAdmin, isCheckout }) => {
                const showChrome = !isAdmin && !isCheckout;
                return (
                <div className="min-h-screen flex flex-col">
                  {showChrome && <AnnouncementBanner />}
                  {showChrome && <Header />}
                  {showChrome && (
                    <div className="lg:sticky lg:top-0 z-50" data-sticky-offset="primary-nav">
                      <Suspense fallback={<div className="h-11 bg-[hsl(107,30%,22%)]" />}><ImprovedNav /></Suspense>
                    </div>
                  )}

                  <main id="main-content" className="flex-1 min-h-[calc(100vh-160px)]" tabIndex={-1}>
                    <ErrorBoundary>
                      <SmoothRoutes>
                        {staticRoutes}
                        {brandRoutes}
                        {productRoutes}
                        {adminRoutes}
                        {categoryRoutes}
                      </SmoothRoutes>
                    </ErrorBoundary>
                  </main>

                  {showChrome && (
                    <DeferredMount timeout={3500}>
                      <Suspense fallback={null}>
                        <div className="cv-auto">
                          <Footer />
                        </div>
                      </Suspense>
                    </DeferredMount>
                  )}
                  {!isAdmin && (
                    <DeferredMount timeout={3000}>
                      <Suspense fallback={null}>
                        <CookieBanner />
                      </Suspense>
                    </DeferredMount>
                  )}
                  {showChrome && (
                    <DeferredMount timeout={4500}>
                      <Suspense fallback={null}>
                        <NotificationPrompt />
                      </Suspense>
                    </DeferredMount>
                  )}
                  {showChrome && (
                    <DeferredMount timeout={4500}>
                      <Suspense fallback={null}>
                        <ExitIntentEmailCapture />
                      </Suspense>
                    </DeferredMount>
                  )}
                </div>
                );
              }}
            </AppShell>

          </CartSyncProvider>
          {AnalyticsPolicyDebugPanel && (
            <Suspense fallback={null}>
              <AnalyticsPolicyDebugPanel />
            </Suspense>
          )}
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
